import {useMemo, useState} from "react";
import {Card, Col, Form, Row, Table} from "react-bootstrap";
import {StatsApi} from "../../api/StatsApi";
import {DatacenterStatsReport, ProductFunnel, SiteStatCount, SiteStatMarker, SiteStatsReport} from "../../api/models";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {usePersistentState} from "../../components/usePersistentState";
import {ChartMarker, ChartPoint, VisitsChart} from "./stats/VisitsChart";
import {COMPARE_WINDOWS, CompareWindow, MarkerComparison, Markers} from "./stats/Markers";

const RANGES = [7, 30, 90, 365] as const;
type Range = typeof RANGES[number];
const isRange = (value: unknown): value is Range => (RANGES as readonly unknown[]).includes(value);

const DAY_MS = 24 * 60 * 60 * 1000;
const dayOf = (date: Date) => date.toISOString().substring(0, 10);
const shortDate = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
const countryNames = new Intl.DisplayNames(undefined, { type: 'region' });
const languageNames = new Intl.DisplayNames(undefined, { type: 'language' });
const percent = new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 1 });

const countryName = (code: string) => {
    try {
        return code === '??' ? 'Unknown' : countryNames.of(code) ?? code;
    } catch {
        return code;
    }
};
const languageName = (code: string) => {
    try {
        return code === '??' ? 'Unknown' : `${languageNames.of(code) ?? code} (${code})`;
    } catch {
        return code;
    }
};
// part ÷ whole, or a dash when there's no whole.
const ratio = (part: number, whole: number) => whole > 0 ? percent.format(part / whole) : '–';

// Days as they are for up to three months; weeks beyond, so bars stay readable.
function chartPoints(daily: SiteStatsReport['daily']): ChartPoint[] {
    if (daily.length <= 92) {
        return daily.map(x => {
            const label = shortDate.format(new Date(`${x.day}T00:00:00Z`));
            return { label, tick: label, value: x.visits };
        });
    }
    const points: ChartPoint[] = [];
    for (let i = 0; i < daily.length; i += 7) {
        const week = daily.slice(i, i + 7);
        const start = shortDate.format(new Date(`${week[0].day}T00:00:00Z`));
        points.push({ label: `Week of ${start}`, tick: start, value: week.reduce((sum, x) => sum + x.visits, 0) });
    }
    return points;
}

// Where the markers' days fall among the chart's bars: the day's own, or its week's.
function chartMarkers(daily: SiteStatsReport['daily'], markers: SiteStatMarker[]): ChartMarker[] {
    const weekly = daily.length > 92;
    return markers.flatMap(marker => {
        const index = daily.findIndex(x => x.day === marker.day);
        return index < 0 ? [] : [{ index: weekly ? Math.floor(index / 7) : index, name: marker.name }];
    });
}

const isCompareWindow = (value: unknown): value is CompareWindow => (COMPARE_WINDOWS as readonly unknown[]).includes(value);

// The site's anonymous visit counts (components/siteStats.ts) and each product's purchase funnel. Counts are of views
// and visits, not people: someone who views a page twice counts twice.
export function AnalyticsPage() {
    const [range, setRange] = usePersistentState<Range>('admin-analytics:range', 30, isRange);
    const {data: report, error, loading} = useAsync(() => {
        const now = Date.now();
        return StatsApi.report(dayOf(new Date(now - (range - 1) * DAY_MS)), dayOf(new Date(now)));
    }, [range]);
    const {data: markers, error: markersError, setData: setMarkers} = useAsync(() => StatsApi.markers(), []);
    const [selected, setSelected] = useState<string>();
    const [compareWindow, setCompareWindow] = usePersistentState<CompareWindow>('admin-analytics:compare-window', 14, isCompareWindow);
    const points = useMemo(() => report ? chartPoints(report.daily) : [], [report]);
    const lines = useMemo(() => report && markers ? chartMarkers(report.daily, markers) : [], [report, markers]);
    const selectedMarker = markers?.find(x => x.id === selected);
    const totals = useMemo(() => report && {
        visits: report.daily.reduce((sum, x) => sum + x.visits, 0),
        pageViews: report.daily.reduce((sum, x) => sum + x.pageViews, 0),
        productViews: report.products.reduce((sum, x) => sum + x.productViews, 0),
        purchased: report.products.reduce((sum, x) => sum + x.purchased, 0)
    }, [report]);

    return (
        <>
            <PageHeader title="Analytics">
                <Form.Select className="w-auto" aria-label="Period" value={range} onChange={e => setRange(Number(e.target.value) as Range)}>
                    {RANGES.map(x => <option key={x} value={x}>Last {x} days</option>)}
                </Form.Select>
            </PageHeader>
            <p className="text-body-secondary small">
                Anonymous daily totals: no addresses, cookies or accounts, and your own visits as an administrator aren't counted.
                Views and visits count each time, not each person. Days are UTC. Visits from data centers (automated browsers,
                nearly all) are counted apart, at the bottom.
            </p>
            {loading && !report && <LoadingSpinner/>}
            <ErrorAlert error={error}/>
            {report && totals && (
                <>
                    <Row xs={2} md={4} className="g-3 mb-4">
                        <Stat label="Visits" value={totals.visits}/>
                        <Stat label="Page views" value={totals.pageViews}/>
                        <Stat label="Product views" value={totals.productViews}/>
                        <Stat label="Purchases" value={totals.purchased} note={`${ratio(totals.purchased, totals.productViews)} of product views`}/>
                    </Row>

                    <h2 className="h5">Visits per {points.length < report.daily.length ? 'week' : 'day'}</h2>
                    <VisitsChart points={points} unit="visits" markers={lines}/>
                    <details className="mb-4">
                        <summary className="small">Show as a table</summary>
                        <Table size="sm" className="mt-2 w-auto">
                            <thead><tr><th>{points.length < report.daily.length ? 'Week' : 'Day'}</th><th className="text-end">Visits</th></tr></thead>
                            <tbody>{points.map(x => <tr key={x.label}><td>{x.label}</td><td className="text-end">{x.value}</td></tr>)}</tbody>
                        </Table>
                    </details>

                    <h2 className="h5">Markers</h2>
                    <p className="text-body-secondary small">
                        Name a day, such as a campaign's launch, to see it on the chart and compare the days from it with as many
                        days before it. It can be added before or after the day.
                    </p>
                    <ErrorAlert error={markersError}/>
                    {markers && <Markers markers={markers} onChange={setMarkers} selected={selected} onSelect={setSelected}/>}
                    {selectedMarker && <MarkerComparison marker={selectedMarker} window={compareWindow} onWindow={setCompareWindow}/>}

                    <h2 className="h5">Purchase funnel</h2>
                    <Funnel products={report.products}/>

                    <Row className="g-4">
                        <Col lg={6}>
                            <h2 className="h5">Countries</h2>
                            <Table size="sm" responsive>
                                <thead><tr><th>Country</th><th className="text-end">Visits</th><th className="text-end">Page views</th></tr></thead>
                                <tbody>
                                {report.countries.map(x => <tr key={x.name}><td>{countryName(x.name)}</td><td className="text-end">{x.visits}</td><td className="text-end">{x.pageViews}</td></tr>)}
                                {report.countries.length === 0 && <Empty columns={3}/>}
                                </tbody>
                            </Table>
                        </Col>
                        <Col lg={6}>
                            <h2 className="h5">Preferred languages</h2>
                            <Table size="sm" responsive>
                                <thead><tr><th>Language</th><th className="text-end">Visits</th><th>From</th></tr></thead>
                                <tbody>
                                {report.languages.map(x => (
                                    <tr key={x.name}>
                                        <td>{languageName(x.name)}</td>
                                        <td className="text-end">{x.count}</td>
                                        <td className="small text-body-secondary">{x.countries.slice(0, 3).map(c => `${countryName(c.name)} ${c.count}`).join(', ')}{x.countries.length > 3 && ', …'}</td>
                                    </tr>
                                ))}
                                {report.languages.length === 0 && <Empty columns={3}/>}
                                </tbody>
                            </Table>
                        </Col>
                        <Col lg={6}><Counts title="Where visits came from" column="Source" counts={report.sources}/></Col>
                        <Col lg={6}><Counts title="Landing pages" column="Page" counts={report.landingPages}/></Col>
                        <Col lg={6}><Counts title="Pages viewed" column="Page" measure="Views" counts={report.pages}/></Col>
                    </Row>

                    {report.datacenter && <Datacenter report={report.datacenter}/>}
                </>
            )}
        </>
    );
}

// Data centers' hits, apart from everything above: what they asked for, when, and how they got there.
function Datacenter({report}: { report: DatacenterStatsReport }) {
    const days = report.daily.filter(x => x.visits || x.pageViews || x.productViews);
    const sum = (field: 'visits' | 'pageViews' | 'productViews') => report.daily.reduce((total, x) => total + x[field], 0);
    return (
        <section className="mt-4 pt-3 border-top">
            <h2 className="h4">Data center traffic</h2>
            <p className="text-body-secondary small">
                Hits from data centers' addresses, whatever browser they claim to be: nearly all automated (scanners,
                crawlers, monitors). They're in none of the numbers above.
            </p>
            {days.length === 0 && !report.downloads?.length ? <p className="text-body-secondary">None in this period.</p> : (
                <>
                    <Row xs={2} md={4} className="g-3 mb-4">
                        <Stat label="Visits" value={sum('visits')}/>
                        <Stat label="Page views" value={sum('pageViews')}/>
                        <Stat label="Product views" value={sum('productViews')}/>
                    </Row>
                    <Row className="g-4">
                        <Col lg={6}>
                            <h3 className="h5">Days with any</h3>
                            <Table size="sm" responsive>
                                <thead><tr><th>Day</th><th className="text-end">Visits</th><th className="text-end">Page views</th><th className="text-end">Product views</th></tr></thead>
                                <tbody>
                                {days.map(x => (
                                    <tr key={x.day}>
                                        <td>{shortDate.format(new Date(`${x.day}T00:00:00Z`))}</td>
                                        <td className="text-end">{x.visits}</td>
                                        <td className="text-end">{x.pageViews}</td>
                                        <td className="text-end">{x.productViews}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </Col>
                        <Col lg={6}><Counts title="Products viewed" column="Product" measure="Views" counts={report.products} level={3}/></Col>
                        {report.downloads && report.downloads.length > 0 &&
                            <Col lg={6}><Counts title="Products downloaded" column="Product" measure="Downloads" counts={report.downloads} level={3}/></Col>}
                        <Col lg={6}><Counts title="Where visits came from" column="Source" counts={report.sources} level={3}/></Col>
                        <Col lg={6}><Counts title="Landing pages" column="Page" counts={report.landingPages} level={3}/></Col>
                        <Col lg={6}><Counts title="Pages viewed" column="Page" measure="Views" counts={report.pages} level={3}/></Col>
                        <Col lg={6}>
                            <Counts title="Countries" column="Country" counts={report.countries.map(x => ({ ...x, name: countryName(x.name) }))} level={3}/>
                        </Col>
                    </Row>
                </>
            )}
        </section>
    );
}

function Stat({label, value, note}: { label: string, value: number, note?: string }) {
    return (
        <Col>
            <Card className="h-100">
                <Card.Body>
                    <div className="small text-body-secondary">{label}</div>
                    <div className="fs-3 fw-semibold">{value}</div>
                    {note && <div className="small text-body-secondary">{note}</div>}
                </Card.Body>
            </Card>
        </Col>
    );
}

function Funnel({products}: { products: ProductFunnel[] }) {
    if (products.length === 0) {
        return <p className="text-body-secondary">No product views or orders in this period.</p>;
    }
    return (
        <Table size="sm" responsive className="mb-4">
            <thead>
            <tr>
                <th>Product</th>
                <th className="text-end">Product views</th>
                <th className="text-end">Release sign-ups</th>
                <th className="text-end">Trial downloads</th>
                <th className="text-end">Signed-in downloads</th>
                <th className="text-end">Checkout views</th>
                <th className="text-end">Checkouts started</th>
                <th className="text-end">Purchased</th>
                <th className="text-end">Conversion</th>
                <th className="text-end">Cancelled at payment</th>
                <th className="text-end">Abandoned</th>
                <th className="text-end">Declined</th>
                <th className="text-end">In progress</th>
                <th className="text-end">Refunded</th>
            </tr>
            </thead>
            <tbody>
            {products.map(x => (
                <tr key={x.sku}>
                    <td>{x.name}</td>
                    <td className="text-end">{x.productViews}</td>
                    <td className="text-end">{x.releaseSignups} <span className="small text-body-secondary">{ratio(x.releaseSignups, x.productViews)}</span></td>
                    <td className="text-end">{x.guestDownloads ?? 0} <span className="small text-body-secondary">{ratio(x.guestDownloads ?? 0, x.productViews)}</span></td>
                    <td className="text-end">{x.downloads ?? 0} <span className="small text-body-secondary">{ratio(x.downloads ?? 0, x.productViews)}</span></td>
                    <td className="text-end">{x.checkoutViews} <span className="small text-body-secondary">{ratio(x.checkoutViews, x.productViews)}</span></td>
                    <td className="text-end">{x.checkoutsStarted}</td>
                    <td className="text-end">{x.purchased}</td>
                    <td className="text-end" title="Purchases ÷ product views, and ÷ checkout views">
                        {ratio(x.purchased, x.productViews)} <span className="small text-body-secondary">/ {ratio(x.purchased, x.checkoutViews)}</span>
                    </td>
                    <td className="text-end">{x.checkoutCancelled}</td>
                    <td className="text-end">{x.abandoned}</td>
                    <td className="text-end">{x.declined}</td>
                    <td className="text-end">{x.inProgress}</td>
                    <td className="text-end">{x.refunded}</td>
                </tr>
            ))}
            </tbody>
            <caption className="small">
                Release sign-ups: confirmed sign-ups to the mailing list to hear when it's released (÷ product views).
                Trial downloads: times one of its files was downloaded by someone signed out, to try it or as a guest
                (÷ product views). Signed-in downloads: by someone signed in, other than an administrator (÷ product views).
                Conversion: purchases ÷ product views / ÷ checkout views. Cancelled at payment: came back from Stripe or
                PayPal without paying. Abandoned: the checkout expired, or is still unpaid after a day. Orders are counted
                on the day they were started.
            </caption>
        </Table>
    );
}

function Counts({title, column, measure = 'Visits', counts, level = 2}: { title: string, column: string, measure?: string, counts: SiteStatCount[], level?: 2 | 3 }) {
    const Heading = level === 2 ? 'h2' : 'h3';
    return (
        <>
            <Heading className="h5">{title}</Heading>
            <Table size="sm" responsive>
                <thead><tr><th>{column}</th><th className="text-end">{measure}</th></tr></thead>
                <tbody>
                {counts.map(x => <tr key={x.name}><td className="text-break">{x.name}</td><td className="text-end">{x.count}</td></tr>)}
                {counts.length === 0 && <Empty columns={2}/>}
                </tbody>
            </Table>
        </>
    );
}

function Empty({columns}: { columns: number }) {
    return <tr><td colSpan={columns} className="text-body-secondary">None in this period.</td></tr>;
}
